<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { activeDiscountAction } from '@/api/functions/discounts/active-discount-action'
import { inactiveDiscountAction } from '@/api/functions/discounts/inactive-discount-action'
import {
  getDiscountDetailsAction,
  getDiscountUsersAction,
  printDiscountUsersAction,
} from '@/api/functions/settings/discounts-action'
import type { ApiResponse } from '@/api/typings/response.typing'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { useJalaaliDate } from '@/composables/useJalaaliDate'
import { useToast } from '@/composables/useToast'
import { apiFileUrl } from '@/configs/urls'
import { getErrorMessage } from '@/utils/errorUtils'
import { numberWithCommas } from '@/utils/number-with-commas'
import DiscountUserCard from './partials/DiscountUserCard.vue'
import UpdateDiscountDetailsModal from './partials/UpdateDiscountDetailsModal.vue'

const route = useRoute()
const router = useRouter()
const { toJalaali } = useJalaaliDate()
const toast = useToast()

const discountId = route.params.id as string
const showUpdateModal = ref(false)
const currentPage = ref(1)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const activeFilters = ref<Record<string, any>>({})
const showInactiveModal = ref(false)
const showActiveModal = ref(false)

const tabs = computed(() => [
  { title: 'کاربران استفاده کرده', link: `/discounts/${discountId}` },
  { title: 'کاربران استفاده نکرده', link: `/discounts/${discountId}/not-used-users` },
])

//  Filter Fields
const filterFields = [
  {
    type: 'input',
    key: 'user_id',
    label: 'کد کاربری را وارد کنید',
    title: 'جستجو',
  },
]

const { currencies } = useGeneralSettings()

const { data: discountResponse, refetch: refetchDiscount } = useQueryApi({
  queryKey: ['discountDetails', discountId],
  queryFn: () => getDiscountDetailsAction(discountId),
  staleTime: 1000 * 60 * 5,
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { data, isLoading, refetch } = useQueryApi<ApiResponse<any>>({
  queryKey: computed(() => ['discountUsers', discountId, currentPage.value, activeFilters.value]),
  queryFn: () => {
    const filters = Object.fromEntries(
      Object.entries(activeFilters.value).filter(([key, value]) => key !== 'page' && value !== false)
    )

    return getDiscountUsersAction(discountId, {
      page: currentPage.value,
      ...filters,
    })
  },
  // keepPreviousData: true,

  staleTime: 1000 * 60 * 3,
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const discount = computed(() => discountResponse.value?.data || ({} as Record<string, any>))
const users = computed(() => data.value?.data || [])
const totalItems = computed(() => data.value?.data?.pagination?.total ?? 0)
const itemsPerPage = computed(() => data.value?.data?.pagination?.perPage ?? 10)

const formatDate = (dateString: string) => {
  if (!dateString) return ''
  const jDate = toJalaali(dateString)
  return `${jDate} `
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onApplyFilters = (values: Record<string, any>) => {
  activeFilters.value = { ...values }
  currentPage.value = 1
  router.push({
    path: route.path,
    query: Object.fromEntries(Object.entries(values).filter(([_, v]) => v)),
  })
  refetch()
}

const onResetFilters = () => {
  activeFilters.value = {}
  currentPage.value = 1
  router.push({ path: route.path, query: {} })
  refetch()
}

const goToPage = (page: number) => {
  currentPage.value = page
  refetch()
}

// Inactive Discount Action
const inActiveMutation = useMutationApi({
  mutationFn: (id: string) => inactiveDiscountAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'تخفیف با موفقیت غیرفعال شد')
    refetchDiscount()
    showInactiveModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleInactiveDiscount = () => {
  if (!discountId) return
  inActiveMutation.mutate(discountId)
}

// Active Discount Action
const activeMutation = useMutationApi({
  mutationFn: (id: string) => activeDiscountAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'تخفیف با موفقیت فعال شد')
    refetchDiscount()
    showActiveModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const printDiscountMutation = useMutationApi({
  mutationFn: (id: string) => printDiscountUsersAction(id),

  onSuccess: (res) => {
    const pdfPath = res?.pdf_path

    if (pdfPath) {
      window.open(apiFileUrl(pdfPath), '_blank')
    } else {
      toast.error('PDF یافت نشد')
    }
  },

  onError: (error: unknown) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handlePrintDiscount = () => {
  if (discountId) {
    printDiscountMutation.mutate(discountId)
  } else {
    toast.error('شناسه تخفیف موجود نیست')
  }
}

const handleActiveDiscount = () => {
  if (!discountId) return
  activeMutation.mutate(discountId)
}

function resetFiltersAndReload() {
  activeFilters.value = {}
  goToPage(1)
  router.push({ path: `/discounts/${discountId}`, query: {} })
}

onMounted(() => {
  if (Object.keys(route.query).length > 0) {
    const { page, ...filters } = route.query

    activeFilters.value = filters

    if (page) {
      currentPage.value = Number(page)
    }
  }
})
</script>

<template>
  <div class="mb-9">
    <ConfirmModal v-model="showInactiveModal" title="غیرفعال کردن تخفیف"
      :message="'آیا از غیرفعال کردن تخفیف اطمینان دارید؟'" confirm-text="بله غیرفعال شود" cancel-text="بازگشت"
      @confirm="handleInactiveDiscount" @cancel="showInactiveModal = false" />

    <ConfirmModal v-model="showActiveModal" title="فعال کردن تخفیف" :message="'آیا از فعال کردن تخفیف اطمینان دارید؟'"
      confirm-text="بله فعال شود" cancel-text="بازگشت" @confirm="handleActiveDiscount"
      @cancel="showActiveModal = false" />

    <UpdateDiscountDetailsModal v-model="showUpdateModal" @cancel="showUpdateModal = false" :currencies="currencies"
      :request="(discountResponse?.data as Record<string, any> | undefined)" />


    <router-link to="/settings/discounts"
      class="w-fit mb-4 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
      <img src="/svg/back-icon.svg" class="me-1" width="22" />
      <span>بازگشت</span>
    </router-link>

    <div class=" flex flex-col lg:flex-row lg:items-center justify-between mb-6">

      <div class="w-full lg:w-auto flex flex-col lg:flex-row lg:items-center gap-8 ">
        <div class="text-[#1b325a] text-[22px]">
          شماره تخفیف<span class="font-fa ms-1">{{ discount?.id }}</span>
        </div>

        <!-- edit & delete  -->
        <div class="w-full lg:w-auto flex flex-row items-center gap-2">
          <Button :isAdmin="true" @click="showUpdateModal = true"
            class=" w-1/2 lg:w-auto px-2  rounded-[10px]  h-10! py-0 text-[13px] font-normal ">
            <span class="text-nowrap flex items-center  justify-center gap-2 ">
              <img width="20" src="/svg/edit-icon.svg" />
              ویرایش
            </span>
          </Button>
          <Button v-if="!discount?.is_deleted" :disabled="discount?.type?.title === 'SYSTEM'"
            @click="showInactiveModal = true" :isAdmin="true" variant="outline"
            class=" w-full text-nowrap lg:w-auto border-2 border-[#0000001f]! rounded-[10px] px-5 h-10! py-0 text-[13px] me-2">
            غیرفعال کردن
          </Button>

          <Button v-if="discount?.is_deleted" @click="showActiveModal = true"
            :disabled="discount?.type?.title === 'SYSTEM'" :isAdmin="true" variant="outline"
            class=" w-full text-nowrap lg:w-auto border-2  rounded-[10px] px-5 h-10! py-0 text-[13px] me-2">
            فعال کردن
          </Button>

        </div>

      </div>
      <Button @click="handlePrintDiscount" :isAdmin="true" variant="outline"
        class="w-full lg:w-auto px-2  border-2  rounded-[10px]  h-10! py-0 text-[13px] font-normal  mt-4 lg:mt-0 ">
        <span class="flex items-center justify-center gap-2 ">
          <img width="19" src="/svg/print-svgrepo-com.svg" />
          چاپ گزارش
        </span>
      </Button>

    </div>

    <div class="w-full flex flex-col  p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl">
      <div class="flex flex-col lg:flex-row justify-between gap-6 lg:gap-[168.73px]">
        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">تخفیف:</span>
            <span class="font-fa text-[#1b325a]">{{ discount?.title }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">نوع تخفیف:</span>
            <span class="text-[#1b325a]">{{ discount?.type?.titleFa }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">مقدار تخفیف (٪):</span>
            <span class=" text-[#1b325a] font-fa">{{ discount?.rate }}</span>
          </div>
        </div>
        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">سقف حجمی تخفیف:</span>
            <span class="font-fa text-[#1b325a]">{{ numberWithCommas(discount?.volume_limit) }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">نوع اعتبار:</span>
            <span class="font-fa text-[#1b325a]">
              {{ discount?.credit_type === 'ONE_TIME_USE' ? ' یک‌بار مصرف' : 'مدت‌دار' }}
            </span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">تاریخ اعتبار:</span>
            <span class="font-fa text-[#1b325a]">
              {{ discount?.expiry_date ? formatDate(discount?.expiry_date) : 'ندارد' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!--  Filters Section -->
    <Filters v-if="users.length > 0" :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters"
      class="mt-6 mb-6" />

    <!-- Tabs  -->
    <Tabs :tabs="tabs" :isAdminPanel="true" class="mt-8" />

    <!-- Main Cards && Pagination  -->
    <div v-if="isLoading" class="text-center py-8 text-gray-500">
      در حال دریافت اطلاعات...
    </div>
    <div v-else>
      <div v-if="users.length > 0" class="mt-8 flex flex-col gap-6 ">
        <DiscountUserCard v-for="(user, index) in users" :key="`card-item-${index}`" :discountCode="discountId"
          :user="user" @confirmUser="refetch" />

        <!-- Pagination -->
        <Pagination v-model:currentPage="currentPage" :itemsPerPage="itemsPerPage" :totalItems="totalItems"
          @update:currentPage="goToPage" :isAdmin="true" />
      </div>
      <EmptyState v-else :onReset="resetFiltersAndReload" />
    </div>

  </div>
</template>
